module Bounded where

import Prelude hiding (div)

import Bound
  ( Scope
  , Var(..)
  , abstract1
  , hoistScope
  , instantiate1
  , subst
  , unScope
  )
import Codensity as Codensity
import Control.Alternative
  ( class Alt
  , class Alternative
  , class Plus
  , alt
  , empty
  )
import Control.Comonad (class Comonad, class Extend)
import Control.Monad.Trans.Class (class MonadTrans)
import Data.Const (Const(..))
import Data.Coyoneda (Coyoneda, hoistCoyoneda, liftCoyoneda, lowerCoyoneda)
import Data.Either (Either(..))
import Data.Eq (class Eq1)
import Data.Exists (Exists, mkExists, runExists)
import Data.Foldable (class Foldable)
import Data.Functor.Compose (Compose(..))
import Data.Generic.Rep (class Generic)
import Data.Identity (Identity(Identity))
import Data.Maybe (Maybe(..), maybe)
import Data.Newtype (class Newtype, unwrap)
import Data.Profunctor.Strong (first)
import Data.Show.Generic (genericShow)
import Data.Traversable (class Traversable)
import Data.Tuple (Tuple(..), fst, snd)
import Expr (Expr)
import Expr as Expr
import FixFunctor (class HFunctor, hmap)
import Halogen
  ( ClassName(ClassName)
  , HalogenM
  , Component
  , mkComponent
  , mkEval
  , put
  )
import Halogen.HTML (HTML, div, span, text)
import Halogen.HTML.Properties (class_)
import Matryoshka (class Corecursive, class Recursive, cata, embed, project)
import Ran (lower')
import Ran as Ran

type Apply a = { function :: BExpr a, argument :: BExpr a }
type Lambda a = { variableName :: String, body :: Scope Unit BExpr a }
data BExpr a
  = Application (Apply a)
  | Abstraction (Lambda a)
  | FreeVariable a

derive instance Generic (BExpr a) _
derive instance Eq1 BExpr
derive instance Eq a => Eq (BExpr a)
derive instance Functor BExpr
derive instance Foldable BExpr
derive instance Traversable BExpr

instance Show a => Show (BExpr a) where
  show x = genericShow x

instance Apply BExpr where
  apply = ap

instance Applicative BExpr where
  pure = FreeVariable

instance Bind BExpr where
  bind (Application app) f = Application
    { function: bind app.function f, argument: bind app.argument f }
  bind (Abstraction abs) f = Abstraction
    { variableName: abs.variableName, body: subst f abs.body }
  bind (FreeVariable x) f = f x

instance Monad BExpr

type AppF :: (Type -> Type) -> Type -> Type
type AppF r a = { function :: r a, argument :: r a }

type AbsF r a = { variableName :: String, body :: Scope Unit (Coyoneda r) a }
data BExprF r a
  = AppF (AppF r a)
  | AbsF (AbsF r a)
  | VarF a

derive instance Generic (BExprF r a) _
derive instance (Eq1 r, Functor r) => Eq1 (BExprF r)
derive instance (Eq1 r, Functor r, Eq a) => Eq (BExprF r a)
derive instance Functor f => Functor (BExprF f)

-- No Show instance for Coyoneda, so no Show instance for BExprF

instance HFunctor BExprF where
  hmap f = h
    where
    h (AppF a) = AppF { function: f a.function, argument: f a.argument }
    h (AbsF l) = AbsF l { body = hoistScope (hoistCoyoneda f) l.body }
    h (VarF x) = VarF x

type HAlg :: forall k. ((k -> Type) -> k -> Type) -> (k -> Type) -> Type
type HAlg f g = f g ~> g

type HCoalg :: forall k. ((k -> Type) -> k -> Type) -> (k -> Type) -> Type
type HCoalg f g = g ~> f g

hhylo :: forall f i o. HFunctor f => HCoalg f i -> HAlg f o -> i ~> o
hhylo c a = r
  where
  r :: i ~> o
  r x = a $ hmap r $ c x

class Nested ::
  forall k. (k -> Type) -> ((k -> Type) -> k -> Type) -> Constraint
class HFunctor f <= Nested t f | t -> f where
  hproject :: HCoalg f t
  hembed :: HAlg f t

newtype NComp :: forall k1 k2. (k1 -> Type) -> (k2 -> k1) -> k2 -> Type
newtype NComp f g a = MkNComp (Compose f g a)

derive instance Generic (NComp f g a) _
derive instance Newtype (NComp f g a) _
derive instance (Eq1 f, Eq1 g) => Eq1 (NComp f g)
derive instance (Eq1 f, Eq1 g, Eq a) => Eq (NComp f g a)
derive instance (Functor f, Functor g) => Functor (NComp f g)

instance (Apply f, Apply g) => Apply (NComp f g) where
  apply f x = MkNComp $ apply (unwrap f) (unwrap x)

instance (Applicative f, Applicative g) => Applicative (NComp f g) where
  pure = MkNComp <<< Compose <<< pure <<< pure

instance (Alt f, Functor g) => Alt (NComp f g) where
  alt preferred fallback = MkNComp $ alt (unwrap preferred) (unwrap fallback)

instance (Plus f, Functor g) => Plus (NComp f g) where
  empty = MkNComp empty

instance (Alternative f, Applicative g) => Alternative (NComp f g)

instance Functor f => HFunctor (NComp f) where
  hmap nt = MkNComp <<< Compose <<< map nt <<< unwrap <<< unwrap

instance (Recursive t f, Corecursive t f) => Nested (Const t) (NComp f) where
  hproject = MkNComp <<< Compose <<< map Const <<< project <<< unwrap
  hembed = Const <<< embed <<< map unwrap <<< unwrap <<< unwrap

instance Nested BExpr BExprF where
  hproject (Application a) = AppF a
  hproject (Abstraction l) = AbsF l { body = hoistScope liftCoyoneda l.body }
  hproject (FreeVariable x) = VarF x

  hembed (AppF a) = Application a
  hembed (AbsF l) = Abstraction l { body = hoistScope lowerCoyoneda l.body }
  hembed (VarF x) = FreeVariable x

hfold :: forall r t f. Nested t f => HAlg f r -> t ~> r
hfold a = f
  where
  f :: t ~> r
  f x = a <<< hmap f $ hproject x

-- | `hfold a . hbuild b = b a`
hbuild :: forall r t f. Nested t f => (forall x. HAlg f x -> r ~> x) -> r ~> t
hbuild f = f hembed

hunfold :: forall r t f. Nested t f => HCoalg f r -> (r ~> t)
hunfold c = u
  where
  u :: r ~> t
  u x = hembed <<< hmap u $ c x

-- | `hdestroy d . hunfold c = c d`
hdestroy ::
  forall r t f. Nested t f => (forall x. HCoalg f x -> x ~> r) -> t ~> r
hdestroy u = u hproject

newtype Ran f g a = MkRan (Ran.Ran f g a)

derive instance Functor (Ran f g)

unRan :: forall f g a b. Ran f g a -> (a -> f b) -> g b
unRan (MkRan ran) = ran

lowerRan :: forall f g. Applicative f => Ran f g ~> g
lowerRan = lower' <<< unRan

type LanFA :: forall k. (k -> Type) -> (k -> Type) -> Type -> k -> Type
type LanFA f g a b = { extract :: f b -> a, seed :: g b }

newtype LanF :: forall k. (k -> Type) -> (k -> Type) -> Type -> k -> Type
newtype LanF f g a b = MkLanF (LanFA f g a b)

mkLanF :: forall f g a b. g b -> (f b -> a) -> LanF f g a b
mkLanF seed extract = MkLanF { extract, seed }

runLanF :: forall f g a b. (g b -> f b) -> LanF f g a b -> a
runLanF pt (MkLanF { seed, extract }) = extract $ pt seed

mapLanF :: forall f g a b c. (a -> b) -> LanF f g a c -> LanF f g b c
mapLanF fun (MkLanF l) = mkLanF l.seed (fun <<< l.extract)

applyLanF ::
  forall f g a b c1 c2.
  Functor f =>
  Apply g =>
  LanF f g (a -> b) c1 ->
  LanF f g a c2 ->
  LanF f g b (Tuple c1 c2)
applyLanF (MkLanF lf) (MkLanF lx) =
  mkLanF (map Tuple lf.seed <*> lx.seed) \ft ->
    lf.extract (fst <$> ft) $ lx.extract (snd <$> ft)

pureLanF :: forall f g a. Applicative g => a -> LanF f g a Unit
pureLanF = mkLanF (pure unit) <<< const

newtype Lan :: forall k. (k -> Type) -> (k -> Type) -> Type -> Type
newtype Lan f g a = MkLan (Exists (LanF f g a))

mkLan :: forall f g a b. g b -> (f b -> a) -> Lan f g a
mkLan seed = MkLan <<< mkExists <<< mkLanF seed

runLan :: forall f g a. (g ~> f) -> Lan f g a -> a
runLan nt (MkLan ex) = runExists (runLanF nt) ex

instance functorLan :: Functor (Lan f g) where
  map fun (MkLan ex) = MkLan $ runExists (mkExists <<< mapLanF fun) ex

instance applyLan :: (Functor f, Apply g) => Apply (Lan f g) where
  apply (MkLan lf) (MkLan lx) =
    MkLan $ runExists (\lff -> runExists (mkExists <<< applyLanF lff) lx) lf

instance applicativeLan :: (Functor f, Applicative g) => Applicative (Lan f g)
  where
  pure = MkLan <<< mkExists <<< pureLanF

newtype Codensity f a = MkCodensity (Codensity.Codensity f a)

unCodensity :: forall f a b. Codensity f a -> (a -> f b) -> f b
unCodensity (MkCodensity codensity) = codensity

derive instance Functor (Codensity f)

instance applyCodensity :: Apply (Codensity f) where
  apply f x = MkCodensity (Codensity.apply (unCodensity f) (unCodensity x))

instance applicativeCodensity :: Applicative (Codensity f) where
  pure = MkCodensity <<< Codensity.pure

instance bindCodensity :: Bind (Codensity f) where
  bind x f =
    MkCodensity (Codensity.bind (unCodensity x) \y -> unCodensity $ f y)

instance monadCodensity :: Monad (Codensity f)

instance monadTrandCodensity :: MonadTrans Codensity where
  lift = MkCodensity <<< bind

ranCodensity :: forall f. Codensity f ~> Ran f f
ranCodensity = MkRan <<< unCodensity

codensityRan :: forall f. Ran f f ~> Codensity f
codensityRan = MkCodensity <<< unRan

newtype Density :: forall k. (k -> Type) -> Type -> Type
newtype Density f a = MkDensity (Lan f f a)

unDensity :: forall f a. Density f a -> Lan f f a
unDensity (MkDensity density) = density

dupDensity :: forall f a. Density f a -> Density f (Density f a)
dupDensity dx = case unDensity dx of
  MkLan ex ->
    runExists (\(MkLanF fx) -> mkDensity (mkDensity fx.extract) fx.seed) ex
  where
  mkDensity :: forall g x b. (g b -> x) -> g b -> Density g x
  mkDensity extract seed =
    MkDensity $ MkLan $ mkExists $ MkLanF { extract, seed }

derive instance Functor (Density f)

instance extendDensity :: Extend (Density f) where
  extend fd = map fd <<< dupDensity

instance comonadDensity :: Comonad (Density f) where
  extract = runLan identity <<< unDensity

instance applyDensity :: Apply f => Apply (Density f) where
  apply df = MkDensity <<< apply (unDensity df) <<< unDensity

instance applicativeDensity :: Applicative f => Applicative (Density f) where
  pure = MkDensity <<< pure

gfold ::
  forall t f c r i o. Nested t f => HAlg f (Ran c r) -> t i -> (i -> c o) -> r o
gfold a = unRan <<< hfold a

gunfold ::
  forall t f c s i o.
  Nested t f =>
  HCoalg f (Lan c s) ->
  s i ->
  (c i -> o) ->
  t o
gunfold ca seed = hunfold ca <<< mkLan seed

ffold :: forall t f a. Nested t f => HAlg f (Codensity (Const a)) -> t a -> a
ffold a t = unwrap $ unCodensity (hfold a t) Const

funfold :: forall t f a. Nested t f => HCoalg f (Density (Const a)) -> a -> t a
funfold ca s = gunfold (hmap unDensity <<< ca <<< MkDensity) (Const s) unwrap

class FFunctor f where
  fmap :: forall a b g. Functor g => (a -> b) -> f g a -> f g b

instance FFunctor BExprF where
  fmap f = x
    where
    x (AppF a) = AppF { function: map f a.function, argument: map f a.argument }
    x (AbsF l) = AbsF l { body = map f l.body }
    x (VarF y) = VarF $ f y

mapNested :: forall t f a b. Nested t f => FFunctor f => (a -> b) -> t a -> t b
mapNested f x = gfold mapAlg x (Identity <<< f)
  where
  mapAlg :: HAlg f (Ran Identity t)
  mapAlg t = MkRan
    \e -> hembed (hmap lowerRan (fmap (unwrap <<< e) t))

boundsCheckClosed :: Expr -> BExpr String
boundsCheckClosed = cata alg
  where
  alg = case _ of
    Expr.ApplyF a -> Application a
    Expr.LambdaF l ->
      Abstraction { variableName: l.binder, body: abstract1 l.binder l.body }
    Expr.VariableF x -> FreeVariable x

--| Convert free variables to variable names to generate a parse tree.
nameBounded ::
  forall identifier. (identifier -> String) -> BExpr identifier -> Expr
nameBounded i b =
  ffold alg $ Expr.Variable <<< i <$> b
  where
  runCodensityConst ::
    forall r a b. Codensity (Const r) a -> (a -> Const r b) -> r
  runCodensityConst cod = unwrap <<< unCodensity cod

  alg :: forall a. BExprF (Codensity (Const Expr)) a -> Codensity (Const Expr) a
  alg = case _ of
    AppF a -> MkCodensity codensity
      where
      codensity :: Codensity.Codensity (Const Expr) a
      codensity f =
        Const
          $ Expr.Application
              { function: runExpr a.function
              , argument: runExpr a.argument
              }
        where
        runExpr cca = runCodensityConst cca f
    AbsF l -> MkCodensity codensity
      where
      codensity :: Codensity.Codensity (Const Expr) a
      codensity f =
        Const $ Expr.Abstraction { binder: l.variableName, body }
        where
        var0 = MkCodensity (const $ Const $ Expr.Variable l.variableName)
        body =
          runCodensityConst
            (instantiate1 var0 $ hoistScope lowerCoyoneda l.body)
            f
    VarF x -> pure x

--| Since there are no free variables, we can 'nameBounded' without coverting
--| them.
nameClosed :: BExpr Void -> Expr
nameClosed = nameBounded absurd

--| Free varaibles are already (out-of-scope) names, so we can 'nameBounded'
--| without converting them.
nameNamed :: BExpr String -> Expr
nameNamed = nameBounded identity

--| Checks to see boundsCheckClosed and nameNamed round-trip
nameCheckClosed :: Expr -> Boolean
nameCheckClosed x = x == nameNamed (boundsCheckClosed x)

--| Human print a BExpr if we can human-print its identifiers
--| NB: Uses nameBounded to generate Expr then prints that.
print :: forall identifier. (identifier -> String) -> BExpr identifier -> String
print printIdent = Expr.print <<< nameBounded printIdent

displayBExpr ::
  forall identifier slot action.
  (identifier -> HTML slot action) ->
  BExpr identifier ->
  HTML slot action
displayBExpr displayFree = d (Left <<< displayFree)
  where
  d ::
    forall id2.
    (id2 -> Either (HTML slot action) (Tuple Int String)) ->
    BExpr id2 ->
    HTML slot action
  d f = df
    where
    df :: BExpr id2 -> HTML slot action
    df (FreeVariable v) = dfv (f v)
      where
      dfv (Left html) = html
      dfv (Right (Tuple i x)) =
        span [ class_ $ ClassName "bounded-variable" ]
          [ text (show i <> " (" <> x <> ")") ]
    df (Abstraction l) =
      div
        [ class_ $ ClassName "bounded-abstraction" ]
        [ span [ class_ $ ClassName "bounded-scope-label" ]
            [ text $ "Scope of " <> l.variableName ]
        , div [ class_ $ ClassName "bounded-scope" ]
            [ d displayVar $ unScope l.body ]
        ]
      where
      displayVar (B _unit) = Right $ Tuple 0 l.variableName
      displayVar (F n) = Left $ d (map (first (_ + 1)) <<< f) n
    df (Application a) =
      div
        [ class_ $ ClassName "bounded-application" ]
        [ div [ class_ $ ClassName "bounded-function" ] [ df a.function ]
        , div [ class_ $ ClassName "bounded-argument" ] [ df a.argument ]
        ]

data BoundsCheckerQuery a = BoundsCheck Expr (BExpr String -> a)

boundsChecker :: forall output m. Component BoundsCheckerQuery Unit output m
boundsChecker = mkComponent
  { initialState: const Nothing
  , render: \mExpr -> div [ class_ $ ClassName "bounds-checker" ] $ maybe []
      ( pure <<< displayBExpr
          ( \unbound -> span
              [ class_ $ ClassName "unbound-variable" ]
              [ text unbound ]
          )
      )
      mExpr
  , eval: mkEval
      { initialize: Nothing
      , receive: const Nothing
      , handleAction: const (pure unit)
      , handleQuery
      , finalize: Nothing
      }
  }
  where
  handleQuery ::
    forall a action slots.
    BoundsCheckerQuery a ->
    HalogenM (Maybe (BExpr String)) action slots output m (Maybe a)
  handleQuery (BoundsCheck expr onBound) = do
    put $ Just bound
    pure <<< Just $ onBound bound
    where
    bound = boundsCheckClosed expr
